import React, { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, X } from 'lucide-react';
import { getMergedVizTypeOptions } from './vizTypeOptions';
import { emitCoachEvent } from '@/coach/coachApi';

const ZONE_LABELS = {
  report_header: 'rpt hdr',
  report_footer: 'rpt ftr',
  header: 'pg hdr',
  footer: 'pg ftr',
};
const VIOLET_ZONES = new Set(['report_header', 'report_footer']);

/**
 * Single visual card in the Visuals tab list. Click to set active; rename, change type, or delete.
 */
export function VisualListCard({
  v, isActive, designerMode, canRemove,
  onSetActive, onUpdate, onRemove,
}) {
  const vizOptions = useMemo(() => getMergedVizTypeOptions(), []);
  const zoneLabel = ZONE_LABELS[v._zone] || v._zone;
  const zoneClass = VIOLET_ZONES.has(v._zone) ? 'bg-violet-500/15 text-violet-400' : 'bg-sky-500/15 text-sky-400';

  return (
    <div
      className={`px-2 py-1.5 rounded-lg text-[10px] cursor-pointer mb-0.5 overflow-hidden ${isActive ? 'bg-primary/15 text-primary border border-primary/20' : 'hover:bg-muted/30 text-muted-foreground'}`}
      onClick={onSetActive}
      data-testid={`visual-card-${v.id}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="truncate flex-1">{v.title || v.vizType}</span>
        {designerMode === 'report' && v._zone && (
          <span className={`text-[7px] px-1 py-0.5 rounded ${zoneClass}`}>{zoneLabel}</span>
        )}
        {v.library_visual_id && (
          <span className="text-[7px] px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400" title="Linked to Visual Library">linked</span>
        )}
      </div>
      <div className="flex items-center gap-1 min-w-0">
        <BarChart3 className="w-3 h-3 flex-shrink-0" />
        <input
          value={v.title || ''}
          onChange={e => onUpdate({ title: e.target.value })}
          className="bg-transparent border-none outline-none text-[10px] min-w-0 flex-1 truncate"
          onClick={e => e.stopPropagation()}
          data-testid={`visual-card-${v.id}-title`}
        />
        <Select value={v.vizType} onValueChange={val => { onUpdate({ vizType: val }); emitCoachEvent('chart_type_selected', 'report-chart-type-picker'); }}>
          <SelectTrigger className="h-5 w-14 text-[8px] rounded border-0 bg-transparent p-0 flex-shrink-0" onClick={e => e.stopPropagation()} data-testid={`visual-card-${v.id}-type`} data-coach-target={isActive ? 'report-chart-type-picker' : undefined}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {vizOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canRemove && (
          <button type="button" onClick={e => { e.stopPropagation(); onRemove(); }} className="text-muted-foreground/70 hover:text-destructive flex-shrink-0" data-testid={`visual-card-${v.id}-remove`}>
            <X className="w-2.5 h-2.5" />
          </button>
        )}
      </div>
      {isActive && (
        <input
          value={v.description || ''}
          onChange={e => onUpdate({ description: e.target.value })}
          className="bg-transparent border-none outline-none w-full text-[8px] text-muted-foreground mt-0.5 pl-4"
          onClick={e => e.stopPropagation()}
          placeholder="Description..."
          data-testid={`visual-card-${v.id}-desc`}
        />
      )}
    </div>
  );
}
